/**
 * Map an API source row onto the shape `SourceViewer` expects.
 *
 * The two disagree about what `type` means. The API's is a coarse
 * *provenance* field — `file | link | text` — while the viewer's is a
 * *format*, because that is what decides which renderer runs. The real format
 * of a file source lives in `mime_type`, so it has to be inspected to pick
 * between the PDF, video and text viewers.
 *
 * Extracted from `LessonLearningLayout` so it can be unit-tested directly: it
 * is the seam where a wrong guess sends a PDF to the plain-text renderer, and
 * that was previously only observable by rendering the whole three-panel page.
 */
import { parseUtcDate } from '@/lib/datetime';
import type { Source } from '@/data/fixtures';
import type { SourceItem as ApiSourceItem } from '@/lib/api-client';

export function normalizeSource(s: Source | ApiSourceItem): Source {
  // Already a viewer-shaped source (mock mode hands these straight through).
  // `source_id` is the discriminator because it is the one field the API shape
  // has and the viewer shape does not.
  if (!('source_id' in s)) return s;

  const viewerType: Source['type'] = (() => {
    if (s.type === 'link') return 'link';
    if (s.type === 'text') return 'text';
    const mime = s.mime_type ?? '';
    if (mime.startsWith('application/pdf')) return 'pdf';
    if (mime.startsWith('video/')) return 'video';
    // Unknown or missing mime on a file source. Text is the safe default:
    // every viewer can render bytes as text, and guessing a richer format
    // wrong produces a blank pane rather than a degraded one.
    return 'text';
  })();

  return {
    id: s.source_id,
    name: s.name,
    type: viewerType,
    url: s.url || undefined,
    size: s.file_size ? `${Math.round(s.file_size / 1024)} KB` : undefined,
    uploadedAt: parseUtcDate(s.created_at),
    // Pass the rich type signals through so the viewer dispatcher can resolve
    // a renderer per file extension rather than relying on the coarse `type`
    // above — `.csv` and `.md` both arrive here as 'text'.
    mime_type: s.mime_type ?? undefined,
    original_filename: s.original_filename ?? undefined,
  };
}
